<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <p class="flex-grow-1" v-if="baseEvent && baseEvent.titles"><b>{{ $t('eventIntro') }}</b> {{baseEvent.titles[currentLanguage.key]}}</p>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <!-- Form -->
                <div class="d-flex flex-wrap">
                    <!-- Images -->
                    <div role="group" class="form-group col-12 col-lg-6">
                    <label class=""> {{ $t('images') }}</label>
                        <GalleryPicker class="gallery-picker" v-model="event.images" :ratioWidth="4" :ratioHeight="3" :max="0" v-bind:class="{ 'is-invalid': errorValidator('Images') === false }"></GalleryPicker>
                    </div>
                    <!-- FeaturedImage -->
                    <div role="group" class="form-group col-12 col-lg-6">
                    <label class=""> {{ $t('featuredImageMandatory') }}</label>
                        <GalleryPicker class="gallery-picker" v-model="event.featuredImage" :ratioWidth="4" :ratioHeight="3" :max="1" v-bind:class="{ 'is-invalid': genericValidator(event.featuredImage, attemptedSubmit) === false || errorValidator('FeaturedImage') === false }"></GalleryPicker>
                    </div>
                    <!-- Titles -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('titlesMandatory') }}</label>
                        <CInput class="m-0" type="text" v-model="event.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(event.titles, attemptedSubmit) && errorValidator('Titles')"></CInput>
                    </div>
                    <!-- TagLines -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('tagLinesMandatory') }}</label>
                        <CInput class="m-0" type="text" v-model="event.tagLines[currentLanguage.key]" :invalid-feedback="errorOutput['TagLines']" :is-valid="genericValidator(event.tagLines, attemptedSubmit) && errorValidator('TagLines')"></CInput>
                    </div>
                    <!-- Categories -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('categoriesMandatory') }}</label>
                        <OptionsDropdown v-model="event.categories" :options="categoriesOptions" :title="$t('categories')" :index="0" :is-valid="genericValidator(event.categories, attemptedSubmit) && errorValidator('Categories')" :invalid-feedback="errorOutput['Categories']"></OptionsDropdown>
                    </div>
                    <!-- files (not required) -->
                    <div role="group" class="form-group col-12 col-md-6">
                        <FilesPicker v-model="event.files" :title="$t('files')" v-bind:class="{ 'is-invalid': errorValidator('Files') === false }" :invalidFeedback="errorOutput['Files']"></FilesPicker>
                    </div>
                    <!-- PublishDate -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('publishDate') }}</label>
                        <datepicker class="form-control" v-model="event.publishDate" placeholder="DD-MM-YYYY" :disabled-dates="disabledDates"></datepicker>
                    </div>
                    <!-- StartDate -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('startDateMandatory') }}</label>
                        <datepicker class="form-control" v-model="event.startDate" placeholder="DD-MM-YYYY" :disabled-dates="disabledDates" v-bind:class="{'is-invalid': genericValidator(event.startDate, attemptedSubmit) == false || errorValidator('StartDate') }"></datepicker>
                    </div>
                    <!-- EndDate -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('endDateMandatory') }}</label>
                        <datepicker class="form-control" v-model="event.endDate" placeholder="DD-MM-YYYY" :disabled-dates="endDisabledDate" v-bind:class="{'is-invalid': genericValidator(event.endDate, attemptedSubmit) == false || errorValidator('EndDate') }"></datepicker>
                    </div>
                    <!-- Price -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('priceMandatory') }}</label>
                        <CInput class="m-0" type="number" v-model="event.price" :invalid-feedback="errorOutput['Price']" :is-valid="genericValidator(event.price, attemptedSubmit) && errorValidator('Price')"></CInput>
                    </div>
                    <!-- Time -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('timeMandatory') }}</label>
                        <CInput class="m-0" type="time" step="1" v-model="event.time" :invalid-feedback="errorOutput['Time']" :is-valid="genericValidator(event.time, attemptedSubmit) && errorValidator('Time')"></CInput>
                    </div>
                    <!-- Contact -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('contactMandatory') }}</label>
                        <CInput class="m-0" type="text" v-model="event.contact" :invalid-feedback="errorOutput['Contact']" :is-valid="genericValidator(event.contact, attemptedSubmit) && errorValidator('Contact')"></CInput>
                    </div>
                    <!-- Latitude -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('latitudeMandatory') }}</label>
                        <CInput class="m-0" type="number" v-model="event.latitude" :invalid-feedback="errorOutput['Latitude']" :is-valid="genericValidator(event.latitude, attemptedSubmit) && errorValidator('Latitude')"></CInput>
                    </div>
                    <!-- Longitude -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('longitudeMandatory') }}</label>
                        <CInput class="m-0" type="number" v-model="event.longitude" :invalid-feedback="errorOutput['Longitude']" :is-valid="genericValidator(event.longitude, attemptedSubmit) && errorValidator('Longitude')"></CInput>
                    </div>
                    <!-- Contents -->
                    <div role="group" class="form-group col-12">
                        <label class=""> {{ $t('contentsMandatory') }}</label>
                        <vue-editor v-model="event.contents[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(event.contents[currentLanguage.key], attemptedSubmit) == false || errorValidator('Contents') }"></vue-editor>
                        <div v-if="errorOutput['Contents']" class="invalid-feedback">{{errorOutput['Contents']}}</div>
                    </div>
                    <!-- Address -->
                    <div role="group" class="form-group col-12">
                        <label class=""> {{ $t('addressMandatory') }}</label>
                        <vue-editor v-model="event.address" v-bind:class="{'is-invalid': genericValidator(event.address, attemptedSubmit) == false || errorValidator('Address') }"></vue-editor>
                        <div v-if="errorOutput['Address']" class="invalid-feedback">{{errorOutput['Address']}}</div>
                    </div>
                </div>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary align-self-end" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { mapState, mapActions } from 'vuex'
import router from '@/router/index';
import { deepClone } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";
import { API_HELPER } from "@/helpers/api.js";
import Event from "@/classes/Event";
import InputGetList from "@/classes/InputGetList";
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import OptionsDropdown from '@/controls/OptionsDropdown.vue'
import GalleryPicker from '@/controls/GalleryPicker.vue'
import FormLoading from '@/controls/FormLoading.vue'
import FilesPicker from '@/controls/FilesPicker.vue'
import Datepicker from 'vuejs-datepicker';
import { VueEditor } from "vue2-editor";

  export default {
        components: { LanguageDropdown, ConfirmationPopUp, OptionsDropdown, FilesPicker, GalleryPicker, FormLoading, Datepicker, VueEditor },
    props:{    
        baseEvent:
        {
            type: Object,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            event: new Event({}, {}, {}, [], [], []),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            disabledDates: {
                to: new Date(new Date().setDate(new Date().getDate()-1))
            },
        }
    },
    mounted: function()
    {
        this.resetEvent();
        this.getEventCategories();
    },
    computed:
    {
        ...mapState('events', ['putEventOutput']),
        ...mapState('events', ['postEventOutput']),
        ...mapState('categories', ['getCategoriesOutput']),
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
             var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

            // Validate multi language fields
            for (const [key, value] of Object.entries(this.event.titles)) { if(!value) { result['Titles'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.event.tagLines)) { if(!value) { result['TagLines'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.event.contents)) { if(!value) { result['Contents'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },
        name: function () {
            return this.baseEvent || this.baseName ? (this.baseEvent ? this.baseEvent.name : this.baseName) : null;
        },
        categoriesInfo: function() {
            return this.getCategoriesOutput && this.getCategoriesOutput.data ? this.getCategoriesOutput.data : [];
        },
        categoriesOptions: function()
        {
            var result = [];

            if(this.categoriesInfo)
            {
                this.categoriesInfo.forEach(category => 
                {
                    result.push({key: category.id, label: category.name });
                });
            }

            return result;
        }, 
        endDisabledDate: function(){
            if(this.disabledDates !== null)
            {
                return {
                    to: new Date(new Date().setDate(new Date().getDate()))
                }
            }
        },
    },
    methods:{
        ...mapActions('categories', ['getCategories']),
        genericValidator,
        errorValidator,
        resetEvent(){
            if(!this.baseEvent)
            {
                var titles = {}; var tagLines = {}; var contents = {}; 
                CONSTANTS.supportedLanguages.forEach(language => { titles[language.key] = ""; tagLines[language.key] = ""; contents[language.key] = "";  });
                this.event = new Event(titles, tagLines, contents, [], [], []);
            }
            else
            {
                this.event = deepClone(new Event, this.baseEvent);  

                // featuredImage
                this.event.featuredImage = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.event.featuredImagePath]), path: this.event.featuredImagePath};
                
                // Images
                var images = [];
                this.event.imagesPath.forEach(function(file)
                {
                    images.push({url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, file]), path: file});
                });
                this.event.images = images;

                // File
                var files = [];
                this.event.filesPath.forEach(function(file)
                {
                    files.push({path: file});
                });
                this.event.files = files;

                // Categories
                this.event.categories = this.event.categories.map(x=> x.id);

            }
        },
        submitClicked(){
            this.attemptedSubmit = true;

            // Emit event
            if(this.event.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.event);
            }
        },
         getEventCategories(){
            const itemsPerPage = 999;
            const currentPage = 1;
            const searchText = null;
            const sorter = 1; // ASC
            const propertyName = "name";

            var input = new InputGetList(itemsPerPage, currentPage, searchText, sorter, propertyName);
            input.type = 1; // type -> Event

            this.getCategories(input);         
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push({name: "Events"});
        },
        onProcessTask: function(val)
        {
            if(val != null)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        }
    },
    watch:
    {
        putEventOutput: function(val)
        {
            this.onProcessTask(val);
        },
        postEventOutput: function(val)
        {
            this.onProcessTask(val);
        },
        baseEvent: function()
        {
            this.resetEvent();
        },
        alertOutput: function()
        {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>
>>>.vdp-datepicker input{
    border: none!important;
}

@media only screen and (max-width: 767px) {
    .main-content {
        flex-direction: column;
    }
}

/deep/.quillWrapper.is-invalid .ql-snow.ql-toolbar,
/deep/.quillWrapper.is-invalid .ql-snow.ql-container
{
    border-color: #e55353;
}
</style> 